import { batch } from 'react-redux';
import i18next from 'i18next';

import * as API from 'services/ErplyAPI/api';
import { getClientCode, getHasRightToViewZReport } from 'reducers/Login';
import {
  CLOSE_DAY,
  LOADING,
  OPEN_DAY,
  SET_POSDAYTOTALS,
} from 'constants/OpenCloseDay';
import { getSelectedPos } from 'reducers/PointsOfSale';
import {
  getCurrentDay,
  getCurrentDayByCurrencyCode,
  getCurrentDays,
  getNotesRequired,
} from 'reducers/OpenCloseDay';
import {
  getAllowOfflineLogin,
  getDayCountedByDrawer,
  getDefaultCurrency,
  getEnabledCurrencies,
  getIsModuleEnabled,
  getSyncBatchToDayOpenings,
} from 'reducers/configs/settings';
import { getEmployeeID } from 'reducers/Payments';

import { getPluginLifecycleHook } from '../reducers/Plugins';
import { modalPages as mp } from '../constants/modalPage';

import { addError, addWarning, dismissType } from './Error';
import { closeBatch } from './integrations/terminal';
import { openModalPage, previousModalPage } from './modalPage';
import { softLogout } from './Login';

const startLoading = { type: LOADING, payload: true };
const stopLoading = { type: LOADING, payload: false };

export const openDay = ({
  pointOfSaleID = undefined,
  openedUnixTime = new Date().getTime() / 1000,
  totalsForCurrencies,
  registerTotalsForCurrencies,
}) => async (dispatch, getState) => {
  dispatch(startLoading);
  const selectedPos = getSelectedPos(getState());
  const isMultiCurrencyEnabled = getIsModuleEnabled('pos_multicurrency')(
    getState(),
  );
  const defaultCurrency = getDefaultCurrency(getState());
  const currencies = getEnabledCurrencies(getState());

  let response = [];
  if (!isMultiCurrencyEnabled) {
    response = await API.openDayV2({
      pointOfSaleID: pointOfSaleID || selectedPos.pointOfSaleID,
      openedUnixTime,
      openedSum:
        registerTotalsForCurrencies[defaultCurrency] ||
        totalsForCurrencies[defaultCurrency] ||
        0,
      clientCode: getClientCode(getState()),
    });
  } else {
    // open day for extra currencies + default currency
    response = await Promise.all(
      currencies.map(currencyCode =>
        API.openDayV2({
          pointOfSaleID: pointOfSaleID || selectedPos.pointOfSaleID,
          openedUnixTime,
          openedSum:
            registerTotalsForCurrencies[currencyCode] ||
            totalsForCurrencies[currencyCode] ||
            0,
          clientCode: getClientCode(getState()),
          currencyCode,
        }),
      ),
    )
      .then(data => data.flat())
      .catch(e => {
        console.error('Failed to open days for all currencies', currencies, e);
        return [];
      });
  }

  batch(() => {
    dispatch({ type: OPEN_DAY, payload: response });
    dispatch(stopLoading);
  });
};

export const checkIsDayOpen = ({ pointOfSaleID }) => async (
  dispatch,
  getState,
) => {
  dispatch(startLoading);
  try {
    if (!pointOfSaleID) {
      return; // NO POS SELECTED;
    }
    const isMultiCurrencyEnabled = getIsModuleEnabled('pos_multicurrency')(
      getState(),
    );
    const employeeID = getEmployeeID(getState());
    let response = [];
    if (!isMultiCurrencyEnabled) {
      response = await API.checkIsDayOpen({
        pointOfSaleID,
        employeeID,
      }).catch(e => {
        console.error(
          'Failed to load open days for',
          { employeeID, pointOfSaleID },
          e,
        );
        return [];
      });
    } else {
      // only check for additional currencies if the default currency day is opened
      const currencies = getEnabledCurrencies(getState());
      response = await Promise.all(
        currencies.map(currencyCode =>
          API.checkIsDayOpen({
            pointOfSaleID,
            currencyCode,
            employeeID,
          }),
        ),
      )
        .then(data => data.flat())
        .catch(e => {
          console.error(
            'Failed to load open days for',
            { employeeID, pointOfSaleID },
            e,
          );
          return [];
        });
    }

    if (response.length > 0) {
      dispatch({
        type: OPEN_DAY,
        payload: response,
      });
    } else {
      dispatch({
        type: OPEN_DAY,
        payload: [],
      });
    }
  } catch (e) {
    dispatch({
      type: OPEN_DAY,
      payload: getAllowOfflineLogin(getState()) ? [{}] : null,
    });
  } finally {
    dispatch(stopLoading);
  }
};

export const getPointOfSaleDayTotals = () => async (dispatch, getState) => {
  dispatch(startLoading);
  const { pointOfSaleID } = getSelectedPos(getState());
  const days = getCurrentDays(getState());
  const defaultCurrency = getDefaultCurrency(getState());
  const isMultiCurrencyEnabled = getIsModuleEnabled('pos_multicurrency')(
    getState(),
  );
  const useDrawer = getDayCountedByDrawer(getState());

  const data = await Promise.all(
    days.map(async ({ openedUnixTime, currencyCode, dayID }) => {
      const amounts = await API.getPointOfSaleDayTotals({
        ...(useDrawer ? { dayID } : { pointOfSaleID }),
        ...(isMultiCurrencyEnabled ? { currencyCode } : {}),
        openedUnixTime,
      });
      return [currencyCode ?? defaultCurrency, amounts];
    }),
  )
    .then(d => Object.fromEntries(d ?? []))
    .catch(e => {
      console.error('Failed to load POS day totals', e);
      return [];
    });
  batch(() => {
    dispatch({ type: SET_POSDAYTOTALS, payload: data });
    dispatch(stopLoading);
  });
  return data;
};

export const closeDay = props => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onCloseDay')(
    getState(),
  );
  try {
    const {
      pointOfSaleID,
      openedUnixTime,
      closedUnixTime = new Date().getTime() / 1000,
      closedSum,
      bankedSum,
      notes,
      currencyCode,
      ...rest
    } = props;
    await dispatch(before(props));
    const selectedPos = getSelectedPos(getState());
    const isMultiCurrencyEnabled = getIsModuleEnabled('pos_multicurrency')(
      getState(),
    );
    const useDrawer = getDayCountedByDrawer(getState());
    const currentDay = isMultiCurrencyEnabled
      ? getCurrentDayByCurrencyCode(
          currencyCode ?? getDefaultCurrency(getState()),
        )(getState())
      : getCurrentDay(getState());
    if (!currentDay) return;
    dispatch(startLoading);

    const closeDayPayload = await dispatch(
      on(props, {
        pointOfSaleID: pointOfSaleID || selectedPos.pointOfSaleID,
        ...(useDrawer && currentDay.dayID
          ? { dayID: currentDay.dayID }
          : {
              openedUnixTime: openedUnixTime || currentDay.openedUnixTime,
            }),
        currencyCode: isMultiCurrencyEnabled ? currencyCode : undefined,
        closedUnixTime,
        closedSum,
        bankedSum,
        notes,
        ...rest,
      }),
    );

    const [closedDay] = await API.closeDay(closeDayPayload);

    batch(() => {
      dispatch({ type: CLOSE_DAY, payload: closedDay });
      dispatch(stopLoading);
    });
    await dispatch(after(props, closedDay));
  } catch (err) {
    console.error('Failed to close day', err);
  }
};

const prepareCloseDay = ({ closedSum, bankedSum, currencyCode, ...rest }) =>
  closeDay({ closedSum, bankedSum, currencyCode, ...rest });

export const closeAllDays = daysToClose => async (dispatch, getState) => {
  try {
    const notesRequired = getNotesRequired(getState());
    const rightToViewZReport = getHasRightToViewZReport(getState());
    if (notesRequired && !daysToClose[0]?.notes) {
      dispatch(
        addWarning(i18next.t('openCloseDay:alerts.notesRequired'), {
          selfDismiss: true,
          errorType: 'EOD',
        }),
      );
      return false;
    }

    if (getSyncBatchToDayOpenings(getState())) {
      dispatch(dismissType('EOD'));
      dispatch(startLoading);
      await dispatch(closeBatch())
        .then(r => {
          if (!r) throw new Error('closeBatch did not return a truthy value');
          return r;
        })
        .catch(e => {
          dispatch(
            addError(i18next.t('openCloseDay:alerts.terminalCloseBatchFail'), {
              selfDismiss: true,
              errorType: 'EOD',
            }),
          );
          throw e;
        })
        .finally(() => dispatch(stopLoading));
    }
    await Promise.all(daysToClose.map(r => dispatch(prepareCloseDay(r))))
      .then(() => dispatch(previousModalPage()))
      .then(() => {
        if (!rightToViewZReport) {
          return dispatch(softLogout());
        }
        return dispatch(
          openModalPage({
            component: mp.ZReport,
            props: { logoutAfterClose: true },
          }),
        );
      });
  } catch (err) {
    console.error('Failed to close all days', err);
  }
  return true;
};
